import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { convertImgurUrl } from '../../utils/imgurUtils';
import { RichTextEditor } from '../RichTextEditor';

export const EditableProjectForm = ({ initialData, onSubmit, submitText = "Guardar Cambios", loading = false, onCancel }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    images: initialData?.images || [{ url: '', description: '', external_link: '', external_link_text: '' }],
    links: initialData?.links || [{ title: '', url: '' }]
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        images: initialData.images?.length > 0 ? initialData.images.map(img => ({
          url: img.url || '',
          description: img.description || '',
          external_link: img.external_link || '',
          external_link_text: img.external_link_text || ''
        })) : [{ url: '', description: '', external_link: '', external_link_text: '' }],
        links: initialData.links?.length > 0 ? initialData.links : [{ title: '', url: '' }]
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Filter out empty images and links
    const cleanedData = {
      ...formData,
      images: formData.images.filter(img => img.url.trim()),
      links: formData.links.filter(link => link.url.trim())
    };
    
    onSubmit(cleanedData);
  };

  const addImage = () => {
    setFormData({
      ...formData,
      images: [...formData.images, { url: '', description: '', external_link: '', external_link_text: '' }]
    });
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const updateImage = (index, field, value) => {
    const newImages = [...formData.images];
    // Auto-convert Imgur URLs when updating the URL field
    if (field === 'url') {
      newImages[index][field] = convertImgurUrl(value);
    } else {
      newImages[index][field] = value;
    }
    setFormData({ ...formData, images: newImages });
  };

  const addLink = () => {
    setFormData({
      ...formData,
      links: [...formData.links, { title: '', url: '' }]
    });
  };

  const removeLink = (index) => {
    setFormData({
      ...formData,
      links: formData.links.filter((_, i) => i !== index)
    });
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...formData.links];
    newLinks[index][field] = value;
    setFormData({ ...formData, links: newLinks });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-2">Título *</label>
        <RichTextEditor
          value={formData.title}
          onChange={(value) => setFormData({...formData, title: value})}
          placeholder="Título del proyecto o acción (puedes usar formato)"
          compact={true}
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-2">Descripción *</label>
        <RichTextEditor
          value={formData.description}
          onChange={(value) => setFormData({...formData, description: value})}
          placeholder="Descripción detallada (puedes usar formato, colores, listas, etc.)"
        />
      </div>

      {/* Images */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <label className="block text-sm font-medium">Imágenes</label>
          <Button type="button" onClick={addImage} size="sm" variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Añadir Imagen
          </Button>
        </div>
        <div className="space-y-3">
          {formData.images.map((image, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Imagen {index + 1}</span>
                {formData.images.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => removeImage(index)}
                    size="sm"
                    variant="ghost"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
              <Input
                value={image.url}
                onChange={(e) => updateImage(index, 'url', e.target.value)}
                placeholder="URL de la imagen o video (ej: https://imgur.com/abc123 o Cloudinary)"
              />
              <Input
                value={image.description}
                onChange={(e) => updateImage(index, 'description', e.target.value)}
                placeholder="Descripción de la imagen/video"
              />
              <div className="border-t pt-2 mt-2">
                <p className="text-xs font-medium text-slate-600 mb-2">🔗 Enlace externo (opcional)</p>
                <Input
                  value={image.external_link || ''}
                  onChange={(e) => updateImage(index, 'external_link', e.target.value)}
                  placeholder="URL (ej: https://instagram.com/... o https://caps.com)"
                  className="mb-2"
                />
                <Input
                  value={image.external_link_text || ''}
                  onChange={(e) => updateImage(index, 'external_link_text', e.target.value)}
                  placeholder="Texto del enlace (ej: Ver en Instagram)"
                />
              </div>
              <p className="text-xs text-slate-500">
                💡 Tip: Puedes pegar enlaces de Imgur o Cloudinary. El enlace externo aparecerá debajo de la imagen.
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Links */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <label className="block text-sm font-medium">Enlaces</label>
          <Button type="button" onClick={addLink} size="sm" variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Añadir Enlace
          </Button>
        </div>
        <div className="space-y-3">
          {formData.links.map((link, index) => (
            <div key={index} className="p-4 border rounded-lg space-y-2">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Enlace {index + 1}</span>
                {formData.links.length > 1 && (
                  <Button
                    type="button"
                    onClick={() => removeLink(index)}
                    size="sm"
                    variant="ghost"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
              <Input
                value={link.title}
                onChange={(e) => updateLink(index, 'title', e.target.value)}
                placeholder="Título del enlace"
              />
              <Input
                value={link.url}
                onChange={(e) => updateLink(index, 'url', e.target.value)}
                placeholder="URL (ej: https://...)"
                type="url"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={loading} className="flex-1">
          {loading ? 'Guardando...' : submitText}
        </Button>
        {onCancel && (
          <Button type="button" onClick={onCancel} variant="outline">
            Cancelar
          </Button>
        )}
      </div>
    </form>
  );
};
